import { CSSProperties, ReactNode } from 'react';

export interface SceneLayoutProps {
  topBar?: ReactNode;
  leftRail?: ReactNode;
  rightRail?: ReactNode;
  anchor?: 'top' | 'center';
  /** How the content area handles overflow. 'auto' shows a scrollbar only when needed (default); 'hidden' clips and lets the scene size itself; 'scroll' always shows a scrollbar. */
  overflow?: 'auto' | 'hidden' | 'scroll';
  maxContentWidth?: number;
  topBarHeight?: number;
  /** Whether the 3-column (rails + content) layout is active. Default true. Consumers typically derive this from a viewport hook (see `useViewportAtLeast`). */
  railsVisible?: boolean;
  safeArea?: boolean;
  background?: string;
  /** Show dashed borders + labels on every area (top bar, rails, content). Matches nonogram's "show ad areas". */
  debugAreas?: boolean;
  className?: string;
  children?: ReactNode;
}

export function SceneLayout({
  topBar,
  leftRail,
  rightRail,
  anchor = 'top',
  overflow = 'auto',
  maxContentWidth,
  topBarHeight,
  railsVisible = true,
  safeArea = true,
  background,
  debugAreas = false,
  className,
  children,
}: SceneLayoutProps) {
  const cssVars: CSSProperties = {};
  if (maxContentWidth != null) (cssVars as Record<string, string>)['--scn-content-max'] = `${maxContentWidth}px`;
  if (topBarHeight != null) (cssVars as Record<string, string>)['--scn-topbar-height'] = `${topBarHeight}px`;
  if (background) (cssVars as Record<string, string>)['--scn-bg'] = background;

  return (
    <div
      className={`scn-root${className ? ` ${className}` : ''}`}
      data-safe-area={safeArea ? 'true' : 'false'}
      data-debug-areas={debugAreas ? 'true' : 'false'}
      data-rails-visible={railsVisible ? 'true' : 'false'}
      style={cssVars}
    >
      {(topBar != null || debugAreas) && (
        <div className="scn-topbar" data-testid="scn-topbar" data-area-label="Top Bar">
          {topBar}
        </div>
      )}
      {(leftRail != null || debugAreas) && (
        <aside
          className="scn-rail scn-rail--left"
          data-testid="scn-rail-left"
          aria-label="Left rail"
          data-area-label="Left Rail"
        >
          {leftRail}
        </aside>
      )}
      <main
        className="scn-content"
        data-testid="scn-content"
        data-anchor={anchor}
        data-overflow={overflow}
        data-area-label="Content"
      >
        <div className="scn-content__inner">{children}</div>
      </main>
      {(rightRail != null || debugAreas) && (
        <aside
          className="scn-rail scn-rail--right"
          data-testid="scn-rail-right"
          aria-label="Right rail"
          data-area-label="Right Rail"
        >
          {rightRail}
        </aside>
      )}
    </div>
  );
}
